'use client';
import { useEffect, useRef } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import type { Account, ReviewQueue } from '@curio/contracts';
import { Badge, Button, Loading, ErrorMessage } from '@curio/ui';
import { ButtonLink } from '../components/button-link';
import { AppRow } from '../components/products';
import { reviewWaitLabel } from './review-wait';
import { useReviewQueueRotation } from './review-queue-rotation';

export function ReviewQueuePanel({
  account,
  queue,
}: {
  account: Account;
  queue: UseQueryResult<ReviewQueue>;
}) {
  const different = useReviewQueueRotation(
    account,
    queue.data?.products.map((product) => product.id) ?? [],
  );
  const wait = reviewWaitLabel(account.rules.reviewWaitSeconds);
  const section = useRef<HTMLElement>(null);
  // Links such as the nav's Review arrive with a hash before this row exists.
  useEffect(() => {
    if (location.hash === '#review-suggestions')
      section.current?.scrollIntoView({ block: 'start' });
  }, []);
  return (
    <section
      ref={section}
      className="panel"
      id="review-suggestions"
      aria-label="Products to review"
    >
      <div className="queue-head">
        <div>
          <h2>Products to review</h2>
          <p>Visit a product and wait {wait} before submitting a review.</p>
        </div>
        <Badge tone="xp">
          <Zap size={12} aria-hidden="true" />+{account.rules.reviewXp} XP · 1
          boost credit each
        </Badge>
      </div>
      <div className="queue-list">
        {queue.isPending ? (
          <Loading />
        ) : queue.data?.products.length ? (
          queue.data.products.map((p) => (
            <AppRow
              product={p}
              key={p.id}
              shots={false}
              action={
                <ButtonLink href={'/review/' + p.slug} variant="tint" size="sm">
                  Start review
                </ButtonLink>
              }
            />
          ))
        ) : (
          <p className="secondary">
            {queue.data?.skippedCount
              ? 'No new products to review right now. You can show skipped products again.'
              : 'No products to review right now.'}
          </p>
        )}
      </div>
      <ErrorMessage error={different.error ?? queue.error} />
      <div className="queue-foot">
        {!!queue.data?.products.length && (
          <Button
            variant="ghost"
            disabled={queue.isFetching || different.isPending}
            onClick={() => different.mutate('skip')}
          >
            {different.isPending && different.variables === 'skip'
              ? 'Finding products…'
              : 'Show different products'}
          </Button>
        )}
        {!!queue.data?.skippedCount && (
          <Button
            variant="ghost"
            disabled={queue.isFetching || different.isPending}
            onClick={() => different.mutate('reset')}
          >
            {different.isPending && different.variables === 'reset'
              ? 'Restoring products…'
              : 'Show skipped products again'}
          </Button>
        )}
      </div>
    </section>
  );
}
